// frontend/src/theme/variants/gradient.ts
// -----------------------------------------------------------------------------
// Athena Gradient Theme
// -----------------------------------------------------------------------------
// Purple-to-orange gradient background (athenatechai.com style)
// Purple accent with dynamic gradient backdrop
// Eye-catching, modern, and energetic variant
// -----------------------------------------------------------------------------

import { alpha, darken, createTheme } from "@mui/material/styles";
import {
  athenaColors,
  glassTokens,
  semanticColors,
  shadowTokens,
  typography,
  contentTextPresets,
} from "../palette";

const mode = "dark";

// Background with full purple-to-orange gradient (website style)

const backgroundGradient = `
  linear-gradient(135deg, 
    #BEB9F2 0%,
    #C5B4D9 25%,
    #FCD9D2 75%,
    #C5B4D9 100%
  )
`;

const darkText = (base: string, amount = 0.55) => darken(base, amount); 


export const athenaGradientTheme = createTheme({
  palette: {
    mode,
    primary: {
      main: athenaColors.deepNavy,
      dark: athenaColors.royalBlue,
      light: athenaColors.periwinkle,
      contrastText: athenaColors.white,
    },
    secondary: {
      main: athenaColors.periwinkle,
      dark: athenaColors.blueGray,
      light: athenaColors.lightPeriwinkle,
      contrastText: athenaColors.deepNavy,
    },
    background: {
      default: athenaColors.deepNavy,
      paper: alpha(athenaColors.white, 0.04),
    },
    text: {
      primary: athenaColors.deepNavy,     // #092047 — dark text on light gradient
      secondary: athenaColors.royalBlue,  // #0b468c — branded secondary
      disabled: athenaColors.blueGray,    // #5a6a8a — muted
    },
    divider: alpha(athenaColors.periwinkle, 0.2),
    ...semanticColors,
    // Custom accent colors
    custom: {
      ...athenaColors,
      accent: athenaColors.softPurple,
      accentLight: athenaColors.lightPurple,
      action: athenaColors.orange,
      actionLight: athenaColors.lightOrange,
    } as any,
  },

  typography: {
    fontFamily: typography.fontFamily,
    h1: {
      fontWeight: 800,
      fontSize: "3rem",
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontWeight: 700,
      fontSize: "2.25rem",
      lineHeight: 1.15,
      letterSpacing: "-0.01em",
    },
    h3: { fontWeight: 700, fontSize: "1.875rem", lineHeight: 1.2 },
    h4: { fontWeight: 600, fontSize: "1.5rem", lineHeight: 1.25 },
    h5: { fontWeight: 600, fontSize: "1.25rem", lineHeight: 1.3 },
    h6: { fontWeight: 600, fontSize: "1.125rem", lineHeight: 1.35 },
    body1: { fontWeight: 400, fontSize: "1rem", lineHeight: 1.6 },
    body2: { fontWeight: 400, fontSize: "0.9375rem", lineHeight: 1.55 },
    caption: { fontSize: "0.8125rem" },
    button: {
      fontWeight: 700,
      textTransform: "none" as const,
      letterSpacing: 0.2,
    },
  },

  shape: {
    borderRadius: 8,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: backgroundGradient,
          backgroundAttachment: "fixed",
          // Joan, 2026-05-27: hide ALL scrollbars site-wide; scroll
          // behavior is preserved. Cleaner reading surface.
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        },
        "::selection": {
          background: alpha(athenaColors.orange, 0.35),
        },
        "html, body, *": {
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        },
        "*::-webkit-scrollbar, *::-webkit-scrollbar-track, *::-webkit-scrollbar-thumb": {
          display: "none",
          width: 0,
          height: 0,
          background: "transparent",
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 14,
          padding: "8px 16px",
          fontWeight: 700,
          textTransform: "none" as const,
          transition: "all 0.25s ease",
        },
        contained: {
          background: `linear-gradient(180deg, ${alpha(
            athenaColors.royalBlue,
            0.95
          )} 0%, ${alpha(athenaColors.royalBlue, 0.85)} 100%)`,
          color: athenaColors.white,
          border: `1px solid ${alpha(athenaColors.white, 0.2)}`,
          "&:hover": {
            background: `linear-gradient(180deg, ${
              athenaColors.royalBlue
            } 0%, ${alpha(athenaColors.royalBlue, 0.9)} 100%)`,
            transform: "translateY(-1px)",
            boxShadow: shadowTokens.elevation2,
          },
        },
        containedSecondary: {
          background: `linear-gradient(180deg, ${
            athenaColors.orange
          } 0%, ${alpha(athenaColors.orange, 0.9)} 100%)`,
          color: athenaColors.white,
          "&:hover": {
            background: `linear-gradient(180deg, ${athenaColors.lightOrange} 0%, ${athenaColors.orange} 100%)`,
          },
        },
        outlined: {
          borderColor: alpha(athenaColors.deepNavy, 0.25),
          color: athenaColors.deepNavy,
          background: alpha(athenaColors.white, 0.15),
          "&:hover": {
            borderColor: athenaColors.softPurple,
            background: alpha(athenaColors.white, 0.3),
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          background: alpha(
            athenaColors.white,
            glassTokens.glassOpacity.medium
          ),
          backdropFilter: glassTokens.blurHeavy,
          borderRadius: 12,
          border: `1px solid ${alpha(
            athenaColors.white,
            glassTokens.borderOpacity.medium
          )}`,
          boxShadow: shadowTokens.elevation3,
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: alpha(
            athenaColors.white,
            glassTokens.glassOpacity.subtle
          ),
          backdropFilter: glassTokens.blurMedium,
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          background: athenaColors.deepNavy,
          backdropFilter: glassTokens.blurMedium,
          borderBottom: `1px solid ${alpha(athenaColors.white, 0.15)}`,
          boxShadow: shadowTokens.elevation2,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
        filled: {
          background: alpha(athenaColors.softPurple, 0.3),
          color: athenaColors.white,
          "&:hover": {
            background: alpha(athenaColors.softPurple, 0.4),
          },
        },
        outlined: {
          borderColor: alpha(athenaColors.deepNavy, 0.3),
          color: athenaColors.deepNavy,
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 3,
          backgroundColor: athenaColors.orange,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none" as const,
          fontWeight: 600,
          color: alpha(athenaColors.deepNavy, 0.55),
          "&.Mui-selected": {
            color: athenaColors.deepNavy,
          },
        },
      },
    },

    // MuiAlert: {
    //   styleOverrides: {
    //     root: {
    //       borderRadius: 12,
    //       backdropFilter: glassTokens.blurLight,
    //     },
    //     standardSuccess: {
    //       background: alpha(semanticColors.success.main, 0.15),
    //       color: semanticColors.success.light,
    //       border: `1px solid ${alpha(semanticColors.success.main, 0.3)}`,
    //     },
    //     standardWarning: {
    //       background: alpha(athenaColors.orange, 0.15),
    //       color: athenaColors.lightOrange,
    //       border: `1px solid ${alpha(athenaColors.orange, 0.3)}`,
    //     },
    //     standardError: {
    //       background: alpha(semanticColors.error.main, 0.15),
    //       color: semanticColors.error.light,
    //       border: `1px solid ${alpha(semanticColors.error.main, 0.3)}`,
    //     },
    //     standardInfo: {
    //       background: alpha(athenaColors.royalBlue, 0.15),
    //       color: athenaColors.periwinkle,
    //       border: `1px solid ${alpha(athenaColors.royalBlue, 0.3)}`,
    //     },
    //   },
    // },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backdropFilter: glassTokens.blurLight,
        },
    
        standardSuccess: {
          background: alpha(semanticColors.success.main, 0.15),
          color: darkText(semanticColors.success.main, 0.55),
          border: `1px solid ${alpha(semanticColors.success.main, 0.3)}`,
        },
    
        standardWarning: {
          background: alpha(athenaColors.orange, 0.15),
          color: darkText(athenaColors.orange, 0.55),
          border: `1px solid ${alpha(athenaColors.orange, 0.3)}`,
        },
    
        standardError: {
          background: alpha(semanticColors.error.main, 0.15),
          color: darkText(semanticColors.error.main, 0.55),
          border: `1px solid ${alpha(semanticColors.error.main, 0.3)}`,
        },
    
        standardInfo: {
          background: alpha(athenaColors.royalBlue, 0.15),
          color: darkText(athenaColors.royalBlue, 0.55),
          border: `1px solid ${alpha(athenaColors.royalBlue, 0.3)}`,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${alpha(
            athenaColors.deepNavy,
            0.95
          )} 0%, ${alpha(athenaColors.royalBlue, 0.9)} 100%)`,
          color: athenaColors.white,
          borderRadius: 12,
          border: `1px solid ${alpha(athenaColors.white, 0.2)}`,
          backdropFilter: glassTokens.blurLight,
          boxShadow: shadowTokens.elevation3,
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(athenaColors.white, 0.95),
          color: athenaColors.deepNavy,
          borderRadius: 8,
          boxShadow: shadowTokens.elevation2,
          fontSize: "0.8125rem",
        },
        arrow: {
          color: alpha(athenaColors.white, 0.95),
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(athenaColors.white, 0.45),
          borderRadius: 14,
          "&:hover": {
            backgroundColor: alpha(athenaColors.white, 0.55),
          },
          "&.Mui-focused": {
            backgroundColor: alpha(athenaColors.white, 0.6),
            boxShadow: `0 0 0 3px ${alpha(athenaColors.softPurple, 0.25)}`,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(athenaColors.deepNavy, 0.2),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(athenaColors.deepNavy, 0.35),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: athenaColors.softPurple,
          },
        },
        input: {
          color: contentTextPresets.onLight.primary,
          "&::placeholder": {
            color: contentTextPresets.onLight.muted,
            opacity: 1,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: contentTextPresets.onLight.secondary,
          "&.Mui-focused": {
            color: athenaColors.softPurple,
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          background: alpha(athenaColors.deepNavy, 0.95),
          backdropFilter: glassTokens.blurHeavy,
          borderRadius: 16,
          border: `1px solid ${alpha(athenaColors.white, 0.2)}`,
          boxShadow: shadowTokens.elevation4,
          color: athenaColors.white,
          // Override input colors inside dark dialogs
          "& .MuiOutlinedInput-root": {
            backgroundColor: alpha(athenaColors.white, 0.08),
            "&:hover": { backgroundColor: alpha(athenaColors.white, 0.12) },
            "&.Mui-focused": { backgroundColor: alpha(athenaColors.white, 0.15) },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(athenaColors.white, 0.25),
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(athenaColors.white, 0.4),
            },
          },
          "& .MuiOutlinedInput-input": {
            color: athenaColors.white,
            "&::placeholder": { color: alpha(athenaColors.white, 0.6), opacity: 1 },
          },
          "& .MuiInputLabel-root": {
            color: athenaColors.periwinkle,
            "&.Mui-focused": { color: athenaColors.orange },
          },
          "& .MuiDialogContentText-root": {
            color: athenaColors.periwinkle,
          },
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          background: alpha(athenaColors.deepNavy, 0.95),
          backdropFilter: glassTokens.blurMedium,
          borderRadius: 12,
          border: `1px solid ${alpha(athenaColors.white, 0.2)}`,
          boxShadow: shadowTokens.elevation3,
          color: athenaColors.white,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: "2px 8px",
          "&:hover": {
            background: alpha(athenaColors.softPurple, 0.15),
          },
          "&.Mui-selected": {
            background: alpha(athenaColors.softPurple, 0.25),
            "&:hover": {
              background: alpha(athenaColors.softPurple, 0.35),
            },
          },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: alpha(athenaColors.white, 0.15),
        },
        bar: {
          borderRadius: 4,
          background: `linear-gradient(90deg, ${athenaColors.softPurple} 0%, ${athenaColors.orange} 100%)`,
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: athenaColors.orange,
        },
      },
    },
  },

  custom: {
    navbarHeight: 64,
    glassShadow: `0 8px 32px ${alpha(athenaColors.deepNavy, 0.25)}`,
    neumorphismBoxShadow: shadowTokens.neumorphicOuter,
    neumorphismInsetShadow: shadowTokens.neumorphicInner,
    circleShadow: `0 20px 60px ${alpha(athenaColors.deepNavy, 0.35)}`,
    circleInnerShadow: `inset 0 8px 24px ${alpha(athenaColors.deepNavy, 0.25)}`,
    // Container opacity tokens - use for all glass container backgrounds
    containerOpacity: glassTokens.containerOpacity,
    // Content text colors - for text on container/glass surfaces
    // athena_gradient has LIGHT backgrounds, so use DARK text
    contentText: contentTextPresets.onLight,
  },
});

export default athenaGradientTheme;
